import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { ADMIN_API_PREFIX, API_PREFIX, BASE_URL } from '@CONSTANTS/API.constant';
import userRepository from '@REPOSITORIES/local-repository/user-repository';
import { ServiceLevel, CreateServiceLevelParams, UpdateServiceLevelParams } from '@INTERFACES/api/service-levels';

// Read API (uses API_PREFIX for read operations)
const serviceLevelsReadApi = createApi({
    reducerPath: 'service-levels-read-api',
    baseQuery: fetchBaseQuery({
        baseUrl: BASE_URL + API_PREFIX,
        prepareHeaders: (headers) => {
            const token = userRepository.getData();

            if (token) {
                headers.set('Authorization', `Bearer ${token}`);
            }

            return headers;
        },
    }),
    tagTypes: ['ServiceLevel'],
    endpoints: (builder) => ({
        getAll: builder.query<ServiceLevel[], void>({
            query: () => '/service-levels/',
            providesTags: ['ServiceLevel'],
        }),
        getById: builder.query<ServiceLevel, number>({
            query: (id) => `/service-levels/${id}/`,
            providesTags: (result, error, id) => [{ type: 'ServiceLevel', id }],
        }),
    }),
});

// Write API (uses ADMIN_API_PREFIX for write operations)
const serviceLevelsWriteApi = createApi({
    reducerPath: 'service-levels-write-api',
    baseQuery: fetchBaseQuery({
        baseUrl: BASE_URL + ADMIN_API_PREFIX,
        prepareHeaders: (headers) => {
            const token = userRepository.getData();

            if (token) {
                headers.set('Authorization', `Bearer ${token}`);
            }

            return headers;
        },
    }),
    tagTypes: ['ServiceLevel'],
    endpoints: (builder) => ({
        create: builder.mutation<ServiceLevel, CreateServiceLevelParams>({
            query: (params) => ({
                url: '/service-levels/',
                method: 'POST',
                body: params,
                headers: {
                    'Content-Type': 'application/json',
                },
            }),
            invalidatesTags: ['ServiceLevel'],
        }),
        update: builder.mutation<ServiceLevel, UpdateServiceLevelParams>({
            query: ({ id, ...params }) => ({
                url: `/service-levels/${id}/`,
                method: 'PUT',
                body: params,
                headers: {
                    'Content-Type': 'application/json',
                },
            }),
            invalidatesTags: (result, error, { id }) => [{ type: 'ServiceLevel', id }],
        }),
        partialUpdate: builder.mutation<ServiceLevel, UpdateServiceLevelParams>({
            query: ({ id, ...params }) => ({
                url: `/service-levels/${id}/`,
                method: 'PATCH',
                body: params,
                headers: {
                    'Content-Type': 'application/json',
                },
            }),
            invalidatesTags: (result, error, { id }) => [{ type: 'ServiceLevel', id }],
        }),
        delete: builder.mutation<void, number>({
            query: (id) => ({
                url: `/service-levels/${id}/`,
                method: 'DELETE',
            }),
            invalidatesTags: ['ServiceLevel'],
        }),
    }),
});

// Export both APIs for middleware and reducer registration
export const serviceLevelsAdminApi = serviceLevelsWriteApi; // Main API for backwards compatibility
export { serviceLevelsReadApi };

// Export hooks from both APIs
export const {
    useGetAllQuery: useGetAllServiceLevels,
    
} = serviceLevelsReadApi;

export const {
    useCreateMutation: useCreateServiceLevel,
    useUpdateMutation: useUpdateServiceLevel,
    
    useDeleteMutation: useDeleteServiceLevel,
} = serviceLevelsWriteApi;
